import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
    setConflictCallback,
    type ConflictDoc,
    type PendingConflict
} from './conflictHandler';
import ConflictDialog from './ConflictDialog';
import { ConflictContext } from './conflictContext';

export function ConflictProvider({ children }: { children: ReactNode }) {
    const [conflicts, setConflicts] = useState<PendingConflict[]>([]);

    const addConflict = useCallback((conflict: PendingConflict) => {
        setConflicts((prev) => [...prev, conflict]);
    }, []);

    const resolveConflict = useCallback(
        (conflictId: string, resolved: ConflictDoc) => {
            setConflicts((prev) => {
                const conflict = prev.find(
                    (candidate) => candidate.id === conflictId
                );
                if (conflict) {
                    conflict.resolve(resolved);
                }
                return prev.filter((candidate) => candidate.id !== conflictId);
            });
        },
        []
    );

    useEffect(() => {
        setConflictCallback(addConflict);
        return () => setConflictCallback(null);
    }, [addConflict]);

    const currentConflict = conflicts.length > 0 ? conflicts[0] : null;

    return (
        <ConflictContext.Provider value={{ conflicts, addConflict }}>
            {children}
            {currentConflict && (
                // A fresh dialog per conflict: the next one in the queue must
                // not inherit the choices made in this one.
                <ConflictDialog
                    key={currentConflict.id}
                    conflict={currentConflict}
                    onResolve={resolveConflict}
                />
            )}
        </ConflictContext.Provider>
    );
}
